<template>
    <section class="max-w-7xl w-full mx-auto px-4 md:pb-16 pb-8 md:pt-8" id="mindflow-gallery">
        <div class="lg:mb-16 mb-8 text-center">
            <h2 class="mb-4 text-3xl font-black md:text-5xl text-mindflow-black">{{ title }}</h2>
            <p class="mx-auto max-w-2xl text-xl font-medium text-gray-600">{{ description }}</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 items-start sm:px-0 px-4">
            <div v-for="(video, index) in videos" :key="video.id"
                class="break-inside-avoid group relative rounded-[2rem] overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer video-wrapper border border-slate-100"
                :class="{ 'md:mt-16': index === 1 || index === 3 }">
                <div class="relative overflow-hidden bg-slate-100 aspect-[9/16] video-container">
                    <div v-if="startedVideoIds.includes(video.id)" class="absolute inset-0 w-full h-full bg-black">
                        <iframe
                            :src="`https://player.vimeo.com/video/${video.id}?autoplay=1&background=0&share=copy&fl=sv&fe=ci`"
                            class="absolute top-0 left-0 w-full h-full" frameborder="0"
                            allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>
                    </div>
                    <div v-else @click="playVideo(video.id)" class="w-full h-full relative cursor-pointer">
                        <img :src="video.poster" :alt="video.name"
                            class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 z-0 poster-image">
                        <div
                            class="play-overlay absolute inset-0 bg-black/5 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center z-10">
                            <div
                                class="w-14 h-14 rounded-full bg-white/90 shadow-xl flex items-center justify-center transform scale-90 group-hover:scale-100 transition-all duration-300">
                                <svg class="w-6 h-6 text-[#F1574B] ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z"></path>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="p-3 bg-white">
                    <h3 class="font-bold text-slate-900 text-base text-center">{{ video.name }}</h3>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
    title: {
        type: String,
        default: 'Real People, Real Results'
    },
    description: {
        type: String,
        default: 'See how Mindflow is changing daily routines across the globe.'
    },
    videos: {
        type: Array,
        default: () => [
            {
                id: '1164462808',
                name: 'Wade Critides',
                poster: '//lifeboostcoffee.com/cdn/shop/files/Wade-Critides-new.png?v=1770833388&width=600'
            },
            {
                id: '1164063256',
                name: 'Cara Molina',
                poster: '//lifeboostcoffee.com/cdn/shop/files/cara-molina-new.png?v=1770833388&width=600'
            },
            {
                id: '1164487949',
                name: 'Carla Purvis',
                poster: '//lifeboostcoffee.com/cdn/shop/files/Carla-Purvis-new.png?v=1770833388&width=600'
            },
            {
                id: '1164463250',
                name: 'Elena Bloomingdale',
                poster: '//lifeboostcoffee.com/cdn/shop/files/Elena_Bloomingdale-new.png?v=1770833388&width=600'
            }
        ]
    }
});

const startedVideoIds = ref([]);

const playVideo = (id) => {
    if (!startedVideoIds.value.includes(id)) {
        startedVideoIds.value.push(id);
    }
};

defineBuilderComponent({
    friendlyName: 'VideosGrid',
    inputs: [
        {
            name: 'title',
            type: 'string',
            defaultValue: 'Real People, Real Results'
        },
        {
            name: 'description',
            type: 'string',
            defaultValue: 'See how Mindflow is changing daily routines across the globe.'
        },
        {
            name: 'videos',
            type: 'list',
            subFields: [
                {
                    name: 'id',
                    type: 'string',
                    defaultValue: '1164061317',
                    helperText: 'Vimeo Video ID'
                },
                {
                    name: 'name',
                    type: 'string',
                    defaultValue: 'John Doe',
                    helperText: 'Name of the person'
                },
                {
                    name: 'poster',
                    type: 'file',
                    allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
                    defaultValue: '//lifeboostcoffee.com/cdn/shop/files/Wade-Critides-new.png?v=1770833388&width=600',
                    helperText: 'Poster image for the video'
                }
            ],
            defaultValue: [
                {
                    id: '1164462808',
                    name: 'Wade Critides',
                    poster: '//lifeboostcoffee.com/cdn/shop/files/Wade-Critides-new.png?v=1770833388&width=600'
                },
                {
                    id: '1164487949',
                    name: 'Cara Molina',
                    poster: '//lifeboostcoffee.com/cdn/shop/files/cara-molina-new.png?v=1770833388&width=600'
                },
                {
                    id: '1164462791',
                    name: 'Carla Purvis',
                    poster: '//lifeboostcoffee.com/cdn/shop/files/Carla-Purvis-new.png?v=1770833388&width=600'
                },
                {
                    id: '1164463250',
                    name: 'Elena Bloomingdale',
                    poster: '//lifeboostcoffee.com/cdn/shop/files/Elena_Bloomingdale-new.png?v=1770833388&width=600'
                }
            ]
        }
    ]
});
</script>